import { DeleteOutlined, KeyOutlined, LockOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { DisableIcon, EnableIcon } from 'assets/images/svg';
import { useManagePermittedRoles } from 'library/hooks';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { selectTeamsByTeamID } from 'reduxStore/teams/selectors';
import {
	DeleteTeamsModal,
	GrantTeamPermissionsModal,
	ManageMembersModal,
	RevokeTeamAccessModal
} from 'zComponents/molecules';
import { Button, Divider, Dropdown, Flex, Tooltip } from 'zui/Antd';

const ManageTeamDropdown = ({ teamID, isTransparent = false }) => {
	const {
		token: {
			colorBgContainer,
			boxShadow,
			borderRadiusLG,
			paddingSM,
			fontSizeLG,
			colorPrimaryText,
			fontWeightStrong
		}
	} = theme.useToken();

	const team = useSelector((state) => selectTeamsByTeamID(state, teamID));
	const canManagePermissions = useManagePermittedRoles('aCPermissionManagement');
	const canManageMembers = useManagePermittedRoles('manageMembers');
	const canDeleteTeam = useManagePermittedRoles('deleteTeam');

	const [openDropdown, setOpenDropdown] = useState(false);

	const items = [
		{
			key: 'Manage Permissions',
			label: (
				<GrantTeamPermissionsModal teamID={teamID}>
					<Button
						type='text'
						icon={<KeyOutlined style={{ color: 'currentColor', fontSize: fontSizeLG }} />}
						block
						disabled={!canManagePermissions}
						style={{
							justifyContent: 'flex-start',
							fontWeight: fontWeightStrong
						}}
					>
						Manage Permissions
					</Button>
				</GrantTeamPermissionsModal>
			)
		},
		{
			key: 'Manage Members',
			label: (
				<ManageMembersModal teamID={teamID}>
					<Button
						type='text'
						icon={<UsergroupAddOutlined style={{ color: 'currentColor', fontSize: fontSizeLG }} />}
						block
						disabled={!canManageMembers}
						style={{
							justifyContent: 'flex-start',
							fontWeight: fontWeightStrong
						}}
					>
						Manage Members
					</Button>
				</ManageMembersModal>
			)
		},
		{
			key: 'divider',
			label: <Divider style={{ margin: 0 }} />
		},
		{
			key: 'Revoke All Access',
			label: (
				<RevokeTeamAccessModal teamID={teamID}>
					<Button
						type='text'
						icon={<LockOutlined style={{ color: 'currentColor', fontSize: fontSizeLG }} />}
						block
						disabled={!canManagePermissions}
						style={{
							justifyContent: 'flex-start',
							fontWeight: fontWeightStrong
						}}
					>
						Revoke All Access
					</Button>
				</RevokeTeamAccessModal>
			)
		},
		...(team?.isActive
			? [
					{
						key: 'Disable Team',
						label: (
							<Tooltip title='Currently working on it' placement='top'>
								<Button
									type='text'
									icon={
										<DisableIcon
											color='currentColor'
											width={fontSizeLG}
											height={fontSizeLG}
											fillOpacity={0.45}
										/>
									}
									block
									disabled
									style={{
										justifyContent: 'flex-start',
										fontWeight: fontWeightStrong
									}}
								>
									Disable Team
								</Button>
							</Tooltip>
						)
					}
				]
			: [
					{
						key: 'Enable Team',
						label: (
							<Tooltip title='Currently working on it' placement='top'>
								<Button
									type='text'
									icon={
										<EnableIcon
											color='currentColor'
											width={fontSizeLG}
											height={fontSizeLG}
											fillOpacity={0.45}
										/>
									}
									block
									style={{
										justifyContent: 'flex-start',
										fontWeight: fontWeightStrong
									}}
									disabled
								>
									Enable Team
								</Button>
							</Tooltip>
						)
					}
				]),
		{
			key: 'Delete Team',
			label: (
				<DeleteTeamsModal teamIDs={[teamID]}>
					<Button
						type='text'
						danger
						icon={<DeleteOutlined style={{ color: 'currentColor', fontSize: fontSizeLG }} />}
						block
						disabled={!canDeleteTeam}
						style={{
							justifyContent: 'flex-start',
							fontWeight: fontWeightStrong
						}}
					>
						Delete Team
					</Button>
				</DeleteTeamsModal>
			)
		}
	];

	return (
		<Dropdown
			trigger={['click']}
			placement='bottomLeft'
			open={openDropdown}
			onOpenChange={(visible) => setOpenDropdown(visible)}
			dropdownRender={() => (
				<Flex
					style={{
						backgroundColor: colorBgContainer,
						borderRadius: borderRadiusLG,
						width: 240,
						boxShadow: boxShadow,
						padding: paddingSM
					}}
					vertical
					gap={paddingSM}
				>
					{items.map((item) => (
						<div key={item.key} onClick={() => setOpenDropdown(false)}>
							{item.label}
						</div>
					))}
				</Flex>
			)}
		>
			<Button type={isTransparent ? 'text' : 'primary'} style={{ color: isTransparent && colorPrimaryText }}>
				{isTransparent ? 'View' : 'Manage'} Team
			</Button>
		</Dropdown>
	);
};

export default ManageTeamDropdown;
